import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import {Users, Car, Building2, Construction, ShoppingBag, Eye, Briefcase, Shield, CheckCircle2, ArrowRight, CircleParking} from 'lucide-react'
import { Link } from "react-router-dom"

function Services() {
  const services = [
    {
      icon: <Users className="w-7 h-7" />,
      title: "Uniformed Security Guards",
      description: "Professional on-site security personnel providing visible deterrence and protection for your property around the clock.",
      features: ["24/7 coverage available", "Licensed & trained guards", "Access control management", "Incident reporting"],
      industries: ["Commercial", "Residential", "Industrial"]
    },
    {
      icon: <Car className="w-7 h-7" />,
      title: "Mobile Patrol Services",
      description: "Regular mobile patrols with comprehensive property checks, ensuring your premises are secure at all hours.",
      features: ["Multiple daily visits", "Detailed patrol reports", "Key holder services", "Alarm response"],
      industries: ["Commercial", "Construction", "Industrial"]
    },
    {
      icon: <Building2 className="w-7 h-7" />,
      title: "Concierge Security",
      description: "Premium security services combining front desk presence with professional hospitality for luxury properties.",
      features: ["Professional front desk presence", "Visitor management", "Package handling", "Customer service excellence"],
      industries: ["Residential", "Hotels", "Corporate"]
    },
    {
      icon: <Construction className="w-7 h-7" />,
      title: "Construction Site Security",
      description: "Protect your construction sites, equipment, and materials from theft, vandalism, and unauthorized access.",
      features: ["Equipment theft prevention", "Site access control", "Vandalism deterrence", "Daily site reports"],
      industries: ["Construction", "Development"]
    },
    {
      icon: <ShoppingBag className="w-7 h-7" />,
      title: "Loss Prevention",
      description: "Expert retail security solutions to reduce shrinkage, protect customers, and maintain a safe shopping environment.",
      features: ["Theft deterrence", "Customer safety", "Inventory protection", "Incident documentation"],
      industries: ["Retail", "Shopping Centers"]
    },
    {
      icon: <Eye className="w-7 h-7" />,
      title: "CCTV & Surveillance",
      description: "Professional monitoring and surveillance systems with real-time incident response and recording capabilities.",
      features: ["24/7 monitoring available", "Remote viewing access", "Incident recording", "System maintenance"],
      industries: ["All Industries"]
    },
    {
      icon: <Briefcase className="w-7 h-7" />,
      title: "Corporate Security",
      description: "Complete security solutions designed for office buildings and corporate environments requiring discretion.",
      features: ["Access control systems", "Visitor management", "Asset protection", "Emergency response"],
      industries: ["Corporate", "Commercial"]
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Executive Protection",
      description: "Discreet personal security for high-profile individuals, executives, and VIPs requiring confidential protection.",
      features: ["Personal bodyguard services", "Risk assessment", "Event security", "Confidentiality assured"],
      industries: ["VIP", "Corporate Executives"]
    },
    {
      icon: <CircleParking className="w-7 h-7" />,
      title: "Parking Management",
      description: "Complete parking facility management including access control, enforcement, traffic flow optimization, and customer assistance.",
      features: ["Access & gate control", "Parking enforcement & compliance", "Traffic flow management", "Revenue & permit administration"],
      industries: ["Commercial", "Residential", "Retail", "Events"]
    }
  ]

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050A14" }}>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden">
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #0A1224, #050A14)" }} />
        <div className="absolute top-0 left-0 w-full h-px" style={{ background: "linear-gradient(to right, transparent, rgba(100,116,139,0.3), transparent)" }} />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-6 block">
            Our Services
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6">
            Comprehensive
            <br />
            <span className="text-slate-600">Security Solutions</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl leading-relaxed">
            Professional security services tailored to your specific needs. From uniformed guards to mobile patrols, we have you covered.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#050A14" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="group p-8 sm:p-10 rounded-lg hover:brightness-125 transition-all relative overflow-hidden"
                style={{ backgroundColor: "#0A1224", border: "1px solid #111D35" }}
              >
                <div className="absolute top-0 left-0 w-0 group-hover:w-full h-px bg-gradient-to-r from-slate-400 to-slate-500 transition-all duration-500" />

                <div className="flex items-start gap-5 mb-6">
                  <div className="flex-shrink-0 w-14 h-14 rounded-lg text-slate-300 flex items-center justify-center group-hover:bg-white group-hover:text-gray-900 transition-all" style={{ backgroundColor: "rgba(30,45,69,0.5)", border: "1px solid #1A2A4A" }}>
                    {service.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2 text-white group-hover:text-slate-200 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed text-sm">
                      {service.description}
                    </p>
                  </div>
                </div>

                <div className="mb-6">
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2.5 text-slate-400 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6" style={{ borderTop: "1px solid #111D35" }}>
                  <div className="flex flex-wrap gap-2">
                    {service.industries.map((industry, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 text-slate-400 text-xs font-medium rounded-md"
                        style={{ backgroundColor: "rgba(17,29,53,0.8)" }}
                      >
                        {industry}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24" style={{ backgroundColor: "#0A1224" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16">
            <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Our Advantage
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Why Choose <span className="text-slate-600">Sentrio?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <Shield className="w-6 h-6" />, title: "Licensed & Insured", description: "All our security personnel are fully licensed and insured across every jurisdiction we operate in." },
              { icon: <Users className="w-6 h-6" />, title: "Trained Professionals", description: "Rigorous training programs ensure our team is prepared for any situation." },
              { icon: <CheckCircle2 className="w-6 h-6" />, title: "Custom Solutions", description: "Every security plan is tailored to meet your specific needs and budget." },
            ].map((item, index) => (
              <div key={index} className="p-8 rounded-lg text-center hover:brightness-125 transition-all group relative overflow-hidden" style={{ backgroundColor: "#050A14", border: "1px solid #111D35" }}>
                <div className="absolute top-0 left-0 w-0 group-hover:w-full h-px bg-gradient-to-r from-slate-400 to-slate-500 transition-all duration-500" />
                <div className="w-14 h-14 rounded-lg text-slate-300 flex items-center justify-center mb-6 mx-auto group-hover:bg-white group-hover:text-gray-900 transition-all" style={{ backgroundColor: "rgba(30,45,69,0.5)", border: "1px solid #1A2A4A" }}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24" style={{ backgroundColor: "#050A14", borderTop: "1px solid #111D35" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6">
            Need Custom <span className="text-slate-400">Security Solutions?</span>
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Let us discuss your security needs and create a customized plan that works for you.
          </p>
          <Link
            to="/contact"
            className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-gray-900 font-semibold text-lg rounded hover:bg-slate-200 transition-all"
          >
            Get Free Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Services
